import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { dismissDsaMissingInfoPopup } from "../../Services/redux/dsa-review/DSAReviewActions";
import closeIcon from "../../assets/images/mfa/close-icon.svg";
import "./DSAMissingInfoPopup.css";

export default function DSAMissingInfoPopup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dialogRef = useRef(null);
  const [isChecked, setIsChecked] = useState(false);
  const showDsaMissingInfoPopup = useSelector(
    (state) => state.dsaReview.showDsaMissingInfoPopup
  );

  useEffect(() => {
    if (showDsaMissingInfoPopup) {
      setIsChecked(false);
    }
  }, [showDsaMissingInfoPopup]);

  const handleDismiss = useCallback(() => {
    dispatch(dismissDsaMissingInfoPopup());
  }, [dispatch]);

  const handleConfirm = useCallback(() => {
    dispatch(dismissDsaMissingInfoPopup());
    navigate("/ui/developer/myprofile", { state: { editMode: true } });
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!showDsaMissingInfoPopup) return;

    const handleClickOutside = (e) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target)) {
        handleDismiss();
      }
    };

    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        handleDismiss();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showDsaMissingInfoPopup, handleDismiss]);

  if (!showDsaMissingInfoPopup || location.pathname.includes("myprofile")) {
    return null;
  }

  return (
    <div className="dsa-missing-overlay">
      <dialog
        className="dsa-missing-dialog"
        aria-labelledby="dsa-missing-title"
        aria-modal="true"
        open
        ref={dialogRef}
      >
        <div className="dsa-missing-header">
          <h2 className="dsa-missing-title" id="dsa-missing-title">
            Action Required: DSA Compliance
          </h2>
          <button
            className="dsa-missing-close-btn"
            aria-label="Close dialog"
            onClick={handleDismiss}
          >
            <img src={closeIcon} alt="" width={16} height={16} />
          </button>
        </div>

        <div className="dsa-missing-body">
          <input
            type="checkbox"
            className="dsa-missing-checkbox"
            id="dsa-checkbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            aria-label="I acknowledge the DSA compliance requirement"
          />
          <p className="dsa-missing-text">
            To publish your app in the European region, you must complete the
            required{" "}
            <a
              className="dsa-link"
              href="https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32022R2065"
              target="_blank"
              rel="noopener noreferrer"
            >
              Digital Services Act (DSA)
            </a>{" "}
            information in your profile. We have saved your current submission
            data. Please visit your profile settings to complete the missing
            fields, then return to submit your app.
          </p>
        </div>

        <div className="dsa-missing-actions">
          <button className="dsa-cancel-btn" onClick={handleDismiss}>
            Cancel
          </button>
          <button
            className={`dsa-confirm-btn${isChecked ? " dsa-confirm-btn--enabled" : ""}`}
            onClick={isChecked ? handleConfirm : undefined}
            disabled={!isChecked}
            aria-disabled={!isChecked}
          >
            Confirm
          </button>
        </div>
      </dialog>
    </div>
  );
}
